import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import WinTextController from "./WinTextController";
import { safeInit } from "../../../utilities";
import {
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
} from "../../../State/selectors";
import { selectWinAnimationCounterFinished } from "../../../State/Slices/animationsSlice";

// The core: sized by its props. WinText (below) feeds it the screen's pixel
// size; the base game view feeds it the native size, so a resize never
// reaches it.
export const WinTextSized = forwardRef(
  (
    // `skin` — basic_win's skin (fire / no_fire); unset = the controller's.
    {
      fontSizeMultiplier = 1.0,
      yPosition = null,
      hideOnBigWin = false,
      skin,
      symbolWidth,
      symbolHeight,
    },
    ref,
  ) => {
    const containerRef = useRef(null);
    const controllerRef = useRef(null);
    const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
    const isAnimatedWin = useSelector(selectIsAnimatedWin);
    const winAnimationCounterFinished = useSelector(selectWinAnimationCounterFinished);

    // Default Y: dead center of the reelset grid.
    // The overlay_reel_container origin (y=0) is the grid center.
    const containerY = useMemo(
      () => (yPosition !== null ? yPosition : 0),
      [yPosition],
    );

    // Initialize controller when container is ready (after render completes)
    useEffect(() => {
      if (containerRef.current && !controllerRef.current) {
        safeInit(() => {
          controllerRef.current = new WinTextController(
            containerRef.current,
            symbolWidth,
            symbolHeight,
            fontSizeMultiplier,
            skin,
          );
        });
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle dimension changes
    useEffect(() => {
      if (controllerRef.current && symbolWidth && symbolHeight) {
        controllerRef.current.updateDimensions(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    useImperativeHandle(ref, () => ({
      showWinAmount: (amount, options) => {
        controllerRef.current?.showWinAmount(amount, options);
      },
      playOut: () => {
        return controllerRef.current?.playOut() || Promise.resolve();
      },
      hide: () => {
        controllerRef.current?.hide();
      },
      getSpineInstance: () => {
        return controllerRef.current?.getSpineInstance();
      },
      // Move the text to a row (native units from the grid centre) — a line's
      // amount sits on its middle-reel cell (2026-10-13). The global total
      // never calls it and stays centred.
      moveTo: (y) => {
        if (containerRef.current && !containerRef.current.destroyed) {
          containerRef.current.y = y;
        }
      },
    }));

    // hideOnBigWin: hidden during a big win until its counter finishes, so the
    // win lines show as soon as the counter ends, not when the popup closes.
    // Alpha 0.001, not visible=false, so the animation cycle keeps running.
    const containerAlpha = useMemo(() => {
      if (hideOnBigWin) {
        const shouldHide = isAnimatedWin && winAnimationsPlaying && !winAnimationCounterFinished;
        return shouldHide ? 0.001 : 1;
      }
      return 1;
    }, [hideOnBigWin, isAnimatedWin, winAnimationsPlaying, winAnimationCounterFinished]);

    return (
      <Container
        ref={containerRef}
        y={containerY}
        x={0}
        zIndex={6}
        sortableChildren
        name={`win_text_${fontSizeMultiplier}`}
        alpha={containerAlpha}
      />
    );
  },
);

WinTextSized.displayName = "WinTextSized";

// The pixel-sized WinText the other rooms use: feeds WinTextSized the
// screen's symbol size.
const WinText = forwardRef((props, ref) => {
  const { symbolWidth, symbolHeight } = useDimensions();
  return (
    <WinTextSized
      ref={ref}
      {...props}
      symbolWidth={symbolWidth}
      symbolHeight={symbolHeight}
    />
  );
});

WinText.displayName = "WinText";

export default WinText;
